import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Empty, Pagination, Switch, Tag } from 'antd';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import {
  loadingStyles,
  noInviteCodesStyles,
  inviteCodesStyles,
} from './InviteCodes.styles';
import {
  fetchInviteCodes,
  selectInviteCodes,
  selectFetchingInviteCodes,
  selectSelectedCircle,
  selectInviteCode,
  updateCircleIfNeeded,
} from '../../../redux/store/userData/circles';
import LoadingIcon from '../../shared/LoadingIcon';
import ListItem from '../../shared/ListItem';
import { openModal } from '../../../redux/store/ui/common';
import { InviteCodeData } from '../../../@types';
import message from '../../../utils/message';
import { useIntl } from 'react-intl';
import { selectBrands } from '../../../redux/store/appData/fleets';
import { selectToken } from '../../../redux/store/userData/user';
import ExportInviteCodesCSV from './ExportInviteCodesCSV';

const ROW_HEIGHT = 48;
const TABLE_HEADER_HEIGHT = 48;
const PAGINATION_HEIGHT = 60;
const MIN_PAGE_SIZE = 5;

const StyledTableCell = withStyles(() => ({
  head: {
    backgroundColor: '#f2f2f2',
    color: '#000',
    fontFamily: 'AvenirNext,sans-serif',
    fontWeight: 'normal',
  },
  body: {
    fontSize: 14,
    color: 'rgba(0, 0, 0, 0.65)',
    fontFamily: 'AvenirNext,sans-serif',
  },
}))(TableCell);

const getStatusColor = (status?: string) => {
  if (!status) return undefined;
  const s = status.toLowerCase();
  if (s === 'active' || s === 'enabled') return 'green';
  if (s === 'accepted') return 'blue';
  if (s === 'used' || s === 'disabled' || s === 'expired' || s === 'blocked')
    return 'red';
  return undefined;
};

const NoInviteCodes = () => {
  const intl = useIntl();
  return (
    <>
      <div>
        <p className="message">
          {intl.formatMessage({
            id: 'fleetadmin.circle.inviteCodes.empty',
            defaultMessage: 'No invite codes for this group',
          })}
        </p>
      </div>
      <style jsx>{noInviteCodesStyles}</style>
    </>
  );
};

/**
 * Invite flags truth table (per API):
 *
 * | invitesEnabled | oneTimeInviteCodesEnabled | Result                    |
 * |----------------|---------------------------|---------------------------|
 * | false          | null                      | neither allowed           |
 * | false          | false                     | neither allowed           |
 * | false          | true                      | neither allowed           |
 * | true           | null                      | only reusable circleCodes |
 * | true           | false                     | only reusable circleCodes |
 * | true           | true                      | only one-time inviteCodes |
 *
 * UI rules derived from the table:
 * - Invite URL (reusable circle-code link) visible only when
 *   invitesEnabled && !oneTimeInviteCodesEnabled
 * - Codes table visible only when
 *   invitesEnabled && oneTimeInviteCodesEnabled
 * - Global Invites Enabled switch is locked only when both are true (prevents
 *   a silent cascade; user must turn one-time off first)
 */
const InviteCodes = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const selectedCircle = useSelector(selectSelectedCircle);
  const inviteCodes = useSelector(selectInviteCodes);
  const fetchingInviteCodes = useSelector(selectFetchingInviteCodes);
  const brands = useSelector(selectBrands);
  const token = useSelector(selectToken);

  const getWebAppDomain = (brandId?: string) =>
    brandId ? brands?.find((b) => b.id === brandId)?.webAppDomain : undefined;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(MIN_PAGE_SIZE);
  const listContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedCircle?.id && !fetchingInviteCodes && inviteCodes === null) {
      dispatch(fetchInviteCodes(selectedCircle.id));
    }
  }, [selectedCircle?.id]);

  // Reset to page 1 when the list changes length (e.g. new codes created)
  useEffect(() => {
    setPage(1);
  }, [selectedCircle?.id]);

  // Measure the list container's actual available height and derive pageSize
  // so rows fit exactly in whatever is left in the drawer (no internal scroll).
  // Re-runs whenever the header content toggles or the window resizes.
  useEffect(() => {
    const el = listContainerRef.current;
    if (!el) return;
    const recalc = () => {
      const available =
        el.clientHeight - TABLE_HEADER_HEIGHT - PAGINATION_HEIGHT;
      const rows = Math.floor(available / ROW_HEIGHT);
      setPageSize(Math.max(MIN_PAGE_SIZE, rows));
    };
    recalc();
    const ro = new ResizeObserver(recalc);
    ro.observe(el);
    return () => ro.disconnect();
  }, [
    selectedCircle?.invitesEnabled,
    selectedCircle?.oneTimeInviteCodesEnabled,
    selectedCircle?.inviteURL,
    // Re-run once the list container is mounted (initially it's null
    // when inviteCodes haven't been fetched yet).
    inviteCodes?.length,
  ]);

  const onAddCodeClicked = () => {
    dispatch(openModal('createInviteCodeModal'));
  };

  const onEditCodeClicked = (code: InviteCodeData) => {
    dispatch(selectInviteCode(code));
    dispatch(openModal('updateInviteCodeModal'));
  };

  const handleCopyKey = (key: string) => {
    navigator.clipboard?.writeText(key);
    message.success(
      intl.formatMessage({
        id: 'fleetadmin.circle.inviteCodes.copied',
        defaultMessage: 'Copied to clipboard',
      })
    );
  };

  const handleCopyDeeplink = (code: InviteCodeData) => {
    if (!code.key) return;
    const webAppDomain = getWebAppDomain(code.brandId);
    if (!webAppDomain) return;
    const domain = webAppDomain.startsWith('http')
      ? webAppDomain
      : `https://${webAppDomain}`;
    const deeplink = `${domain}/circle/join/?id=${encodeURIComponent(
      code.key
    )}`;
    navigator.clipboard?.writeText(deeplink);
    message.success(
      intl.formatMessage({
        id: 'fleetadmin.circle.inviteCodes.deeplinkCopied',
        defaultMessage: 'Deeplink copied to clipboard',
      })
    );
  };

  const handleInvitesEnabledChange = (checked: boolean) => {
    dispatch(
      updateCircleIfNeeded(token, selectedCircle?.id!, {
        invitesEnabled: checked,
      })
    );
  };

  const handleCopyInviteUrl = (url: string) => {
    navigator.clipboard?.writeText(url);
    message.success(
      intl.formatMessage({
        id: 'fleetadmin.circle.inviteCodes.copied',
        defaultMessage: 'Copied to clipboard',
      })
    );
  };

  const handleOneTimeInviteCodesEnabledChange = (checked: boolean) => {
    dispatch(
      updateCircleIfNeeded(token, selectedCircle?.id!, {
        oneTimeInviteCodesEnabled: checked,
      })
    );
    // Re-fetch invite codes when turning feature back on
    if (checked && selectedCircle?.id) {
      dispatch(fetchInviteCodes(selectedCircle.id));
    }
  };

  if (fetchingInviteCodes) {
    return (
      <>
        <div className="loading-wrapper">
          <LoadingIcon />
        </div>
        <style jsx>{loadingStyles}</style>
      </>
    );
  }

  const headCells = [
    {
      id: 'key',
      label: intl.formatMessage({
        id: 'fleetadmin.circle.inviteCodes.keyColumn',
        defaultMessage: 'Key',
      }),
      style: { padding: '12px 4px', borderTopLeftRadius: 8, maxWidth: 100 },
    },
    {
      id: 'description',
      label: intl.formatMessage({
        id: 'fleetadmin.circle.inviteCodes.description',
        defaultMessage: 'Description',
      }),
      style: { padding: '12px 4px', maxWidth: 100 },
    },
    {
      id: 'status',
      label: intl.formatMessage({
        id: 'fleetadmin.circle.inviteCodes.status',
        defaultMessage: 'Status',
      }),
      style: { padding: '12px 4px' },
    },
    {
      id: 'actions',
      label: intl.formatMessage({
        id: 'fleetadmin.circle.inviteCodes.actions',
        defaultMessage: 'Actions',
      }),
      style: { padding: '12px 4px', borderTopRightRadius: 8 },
    },
  ];

  const total = inviteCodes?.length || 0;
  const pagedData =
    inviteCodes?.slice((page - 1) * pageSize, page * pageSize) || [];

  return (
    <>
      <div className="invite-codes-tab-wrapper">
        <div className="invites-header">
          <ListItem
            testid="invitesEnabled"
            label={intl.formatMessage({
              id: 'circle.invitesEnabled',
              defaultMessage: 'Invites Enabled',
            })}
            value={
              <Switch
                checked={selectedCircle?.invitesEnabled}
                onChange={handleInvitesEnabledChange}
                disabled={
                  selectedCircle?.invitesEnabled &&
                  selectedCircle?.oneTimeInviteCodesEnabled
                }
              />
            }
          />
          {selectedCircle?.invitesEnabled &&
            !selectedCircle?.oneTimeInviteCodesEnabled &&
            selectedCircle?.inviteURL && (
              <ListItem
                testid="inviteURL"
                label={intl.formatMessage({
                  id: 'circle.invitesURL',
                  defaultMessage: 'Invite URL',
                })}
                value={
                  <div className="invite-url-wrapper">
                    <span
                      className="invite-url"
                      title={selectedCircle.inviteURL}
                    >
                      {selectedCircle.inviteURL}
                    </span>
                    <img
                      src="/images/car-editor/link.svg"
                      alt="copy link"
                      onClick={() =>
                        handleCopyInviteUrl(selectedCircle.inviteURL!)
                      }
                      style={{ cursor: 'pointer', width: 20, height: 20 }}
                    />
                  </div>
                }
              />
            )}
          <ListItem
            testid="oneTimeInviteCodesEnabled"
            label={intl.formatMessage({
              id: 'circle.oneTimeInviteCodesEnabled',
              defaultMessage: 'One-time Invite Codes Enabled',
            })}
            value={
              <Switch
                checked={selectedCircle?.oneTimeInviteCodesEnabled}
                onChange={handleOneTimeInviteCodesEnabledChange}
                disabled={!selectedCircle?.invitesEnabled}
              />
            }
          />
        </div>
        {selectedCircle?.invitesEnabled &&
          selectedCircle?.oneTimeInviteCodesEnabled && (
            <>
              <div className="invite-codes-toolbar">
                <ExportInviteCodesCSV
                  data={inviteCodes}
                  circleCode={selectedCircle?.circleCode}
                  brands={brands}
                />
                <div
                  className="add-code-button add-code-button--right"
                  onClick={onAddCodeClicked}
                >
                  <p
                    data-testid="InviteCodes-addCodeBtn"
                    className="button-text"
                  >
                    {intl.formatMessage({
                      id: 'fleetadmin.circle.inviteCodes.addCode',
                      defaultMessage: 'Add Code',
                    })}
                  </p>
                </div>
              </div>
              {total === 0 ? (
                <Empty
                  style={{ marginTop: 24 }}
                  description={intl.formatMessage({
                    id: 'fleetadmin.circle.inviteCodes.empty',
                    defaultMessage: 'No invite codes for this group',
                  })}
                />
              ) : (
                <div
                  ref={listContainerRef}
                  data-testid="InviteCodes-codesList"
                  className="invite-codes-list"
                >
                  <TableContainer>
                    <Table aria-label="invite codes table" size="medium">
                      <TableHead>
                        <TableRow>
                          {headCells.map((h) => (
                            <StyledTableCell
                              key={h.id}
                              align="left"
                              padding="none"
                              style={h.style}
                            >
                              {h.label}
                            </StyledTableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedData.map((code) => (
                          <TableRow
                            key={code.id}
                            data-testid={`InviteCodes-row-${code.key}`}
                          >
                            <StyledTableCell
                              align="left"
                              padding="none"
                              style={{
                                padding: '12px 4px',
                                maxWidth: 100,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={code.key}
                            >
                              {code.key || '-'}
                            </StyledTableCell>
                            <StyledTableCell
                              align="left"
                              padding="none"
                              style={{
                                padding: '12px 4px',
                                maxWidth: 100,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={code.description}
                            >
                              {code.description || '-'}
                            </StyledTableCell>
                            <StyledTableCell
                              align="left"
                              padding="none"
                              style={{ padding: '12px 4px' }}
                            >
                              {code?.status ? (
                                <Tag color={getStatusColor(code.status)}>
                                  {code.status}
                                </Tag>
                              ) : (
                                '-'
                              )}
                            </StyledTableCell>
                            <StyledTableCell
                              align="left"
                              padding="none"
                              style={{ padding: '12px 4px' }}
                            >
                              <div className="invite-code-actions">
                                <img
                                  src="/images/car-editor/link.svg"
                                  alt="copy deeplink"
                                  onClick={() => handleCopyDeeplink(code)}
                                  style={{
                                    cursor: 'pointer',
                                    width: 20,
                                    height: 20,
                                  }}
                                />
                                <img
                                  src="/images/car-editor/location.svg"
                                  alt="copy"
                                  onClick={() => handleCopyKey(code.key!)}
                                  style={{
                                    cursor: 'pointer',
                                    width: 20,
                                    height: 20,
                                  }}
                                />
                                <img
                                  className="edit-button"
                                  src="/images/drawer/edit.svg"
                                  alt="edit"
                                  onClick={() => onEditCodeClicked(code)}
                                  style={{ width: 20, height: 20 }}
                                />
                              </div>
                            </StyledTableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {total > pageSize && (
                    <div className="invite-codes-pagination">
                      <Pagination
                        current={page}
                        total={total}
                        pageSize={pageSize}
                        showSizeChanger={false}
                        onChange={(p) => setPage(p)}
                      />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
      </div>
      <style jsx>{inviteCodesStyles}</style>
    </>
  );
};

export default InviteCodes;
